import type { NotificationType } from '@/enums/NotiicationType'
import type { ModelMeta } from '@/models'
import { Role } from '@/models'
import { Model } from '@/plugins/http'

export type UserIncludes = 'fields' | 'emails' | 'phones' | 'addresses' | 'roles'

export type UserMetaIncludes = never

export type UserSorts = never

export type UserFilters = {
  current?: boolean
  is_active?: boolean
}

export type UserCreatePayload = {
  first_name?: string
  last_name?: string
  email?: string
}

export type UserUpdatePayload = {
  avatar?: string
  first_name?: string
  infix_name?: string
  last_name?: string
  email?: string
  is_active?: boolean
  language?: string
  password?: string
  password_current?: string
  notifications?: Record<NotificationType, boolean> | null
  // Role ids. UserRepository::updateRoles() only syncs these for a caller holding
  // admin.users, so sending them from a non-admin session is silently ignored.
  roles?: string[]
}

export type UserResetPasswordPayload = {
  email: string
  token: string
  password: string
  password_confirmation: string
}

export type UserPutPayload = {
  never: never
}

export class User extends Model<UserFilters, UserCreatePayload, UserUpdatePayload, UserPutPayload, UserIncludes, UserMetaIncludes, UserSorts> {
  protected $endpoint = '/v0/users'
  protected $primaryKey: keyof this = 'id'
  protected $fillable: (keyof this)[] = []

  public static includes: UserIncludes[] = ['fields', 'emails', 'phones', 'addresses', 'roles']

  public readonly id!: string
  public email!: string
  public name!: string
  public first_name!: string
  public last_name!: string
  public avatar!: string
  public is_active!: boolean
  public is_root?: boolean
  public created_at!: string
  public updated_at!: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public favorites: any // TODO???
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public restrictions: any // TODO????

  public roles?: Role[]

  public permission?: string // TODO???
  public meta!: ModelMeta

  public static current(): Promise<User | null> {
    try {
      return new User().withoutErrorResolver().filter({ current: true }).first() as unknown as Promise<User | null>
    } catch {
      return Promise.resolve(null)
    }
  }
}

export type AdressLabel = {
  id: string
  created_at?: string | null
  name?: string | null
  type?: string | null
  updated_at?: string | null
}

export type Adress = {
  id: string
  city?: string | null
  country_code?: string | null
  is_primary?: boolean | null
  label?: AdressLabel | null
  number_addition?: string | null
  number_letter?: string | null
  number?: string | null
  organization?: string | null
  post_code?: string | null
  state?: string | null
  street?: string | null
}

export type Email = {
  id: string
  created_at?: string | null
  is_primary?: boolean | null
  label?: string | null
  value?: string | null
}

export type FieldGroup = {
  id: string
  name?: string | null
  sort_order?: number | null
}

export type ProxySource = {
  id: string
  name: string
  email: string | null
  phone: string | null
}

export type FieldOption = never

export type FieldType =
  | 'checkbox'
  | 'currency'
  | 'date'
  | 'number'
  | 'radio'
  | 'select'
  | 'switch'
  | 'text_area'
  | 'text'
  | 'url'
  | 'custom_list'
  | 'entity'

export type Field = {
  id: string
  is_editable?: boolean | null
  label?: string | null
  options?: FieldOption[] | null
  type?: FieldType | null
  value?: string[] | string | null
  human?: string[] | string | null
}

export type Phone = {
  id: string
  created_at?: string | null
  is_primary?: boolean | null
  label?: string | null
  value?: string | null
}
